import React, { useMemo, useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import {
  OUTLOOK_VALUES,
  countAdvancedOverrides,
  resolveAdvancedYearProfile,
  MAX_RETURN_HORIZON_YEARS,
  RETURN_ASSUMPTION_PRESET_IDS,
  RETURN_ASSUMPTION_PRESETS,
  cloneReturnAssumptions,
  matchReturnAssumptionPreset,
} from '../../utils/returnHypothesisMath';
import AdvancedReturnsModal from './AdvancedReturnsModal';
import EmptyableNumberInput from '../EmptyableNumberInput';

function formatMoney(value, locale = 'fr-CA') {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function projectedAmountFromHint(hint) {
  if (!hint) return null;
  if (hint.kind === 'revenueBreakdown') return hint.effectiveAtYear ?? hint.grossAtYear;
  if (hint.kind === 'amountAtYear') return hint.amountAtYear;
  if (hint.kind === 'capRateDerived') return hint.amountAtYear;
  return null;
}

function ModeToggle({ modes, activeMode, onChange, disabled }) {
  return (
    <div className="flex rounded border border-gray-200 overflow-hidden text-xs shrink-0">
      {modes.map((m) => (
        <button
          key={m.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(m.id)}
          className={`px-2 py-1 min-w-[2.25rem] ${
            activeMode === m.id
              ? 'bg-green-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          } disabled:opacity-50`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function MetricWithInfo({ label, value, info, formatPercent, open, onToggle }) {
  return (
    <div className="text-center relative">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center justify-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <span>{label}</span>
        <Info className="w-3.5 h-3.5 shrink-0" aria-hidden />
      </button>
      {open ? (
        <div className="absolute z-10 left-1/2 -translate-x-1/2 mt-2 w-64 p-2 bg-white border rounded shadow-lg text-xs text-gray-700 text-left">
          {info}
        </div>
      ) : null}
      <p className="font-semibold text-gray-900 mt-1">{formatPercent(value)}</p>
    </div>
  );
}

function AssumptionCard({
  label,
  helpText,
  projectedLabel,
  projectedValue,
  modes,
  assumption,
  onAssumptionChange,
  disabled,
}) {
  const mode = assumption?.mode || 'percent';

  const handleMode = (nextMode) => {
    if (nextMode === 'percent') {
      onAssumptionChange({ mode: 'percent', percent: assumption?.percent ?? 0 });
    } else if (nextMode === 'amount') {
      onAssumptionChange({ mode: 'amount', amount: assumption?.amount ?? 0 });
    } else if (nextMode === 'capRate') {
      onAssumptionChange({ mode: 'capRate', capRate: assumption?.capRate ?? 5 });
    }
  };

  const inputValue =
    mode === 'percent'
      ? assumption?.percent ?? ''
      : mode === 'amount'
        ? assumption?.amount ?? ''
        : assumption?.capRate ?? '';

  const step = mode === 'amount' ? 1000 : 0.1;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 h-full flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h4 className="font-medium text-gray-900">{label}</h4>
          <p className="text-xs text-gray-500 mt-0.5">{helpText}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500">{projectedLabel}</p>
          <p className="font-semibold text-blue-700">{formatMoney(projectedValue)}</p>
        </div>
      </div>

      <div className="mt-auto flex gap-2 items-stretch">
        <ModeToggle modes={modes} activeMode={mode} onChange={handleMode} disabled={disabled} />
        <EmptyableNumberInput
          step={step}
          value={inputValue}
          disabled={disabled}
          onChange={(val) => {
            if (mode === 'percent') {
              onAssumptionChange({ mode: 'percent', percent: val });
            } else if (mode === 'amount') {
              onAssumptionChange({ mode: 'amount', amount: val });
            } else {
              onAssumptionChange({ mode: 'capRate', capRate: val });
            }
          }}
          className="flex-1 min-w-0 px-2 py-1.5 border rounded disabled:bg-gray-50"
        />
        {mode === 'percent' ? (
          <span className="text-sm text-gray-500 shrink-0 self-center">%</span>
        ) : null}
      </div>
    </div>
  );
}

const OUTLOOK_I18N = {
  pessimistic: 'propertyReport.futureReturns.outlook.pessimistic',
  realistic: 'propertyReport.futureReturns.outlook.realistic',
  optimistic: 'propertyReport.futureReturns.outlook.optimistic',
};

export default function FutureReturnsSection({
  returnYears,
  onReturnYearsChange,
  returnMode,
  onReturnModeChange,
  assumptions,
  onAssumptionsChange,
  advancedSchedule,
  onAdvancedScheduleChange,
  hypothesisBases,
  onPrefillAdvancedFromSimple,
  onResetAdvancedSchedule,
  hints,
  readOnly,
  hasSubScenario,
  subScenarios,
  selectedSubScenarioId,
  onSubScenarioChange,
  typeLabels,
  returnProjections,
  selectedProjectionId,
  onLoadProjection,
  onClearProjection,
  onSaveProjection,
  onDeleteProjection,
  multiYearReturn,
  multiYearAnnualized,
  multiYearIRR,
  formatPercent,
  t,
  language,
}) {
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  const en = language === 'en';
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [advancedModalOpen, setAdvancedModalOpen] = useState(false);
  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);
  const [draftSchedule, setDraftSchedule] = useState(advancedSchedule);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveOutlook, setSaveOutlook] = useState('realistic');
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openMetricInfo, setOpenMetricInfo] = useState(null);

  const pctMode = { id: 'percent', label: '%' };
  const dollarMode = { id: 'amount', label: '$' };
  const capMode = { id: 'capRate', label: t('propertyReport.futureReturns.modeCapRate') };

  const activePreset = useMemo(
    () => (returnMode === 'simple' ? matchReturnAssumptionPreset(assumptions) : null),
    [returnMode, assumptions],
  );

  const sortedProjections = [...(returnProjections || [])].sort((a, b) => {
    const oa = OUTLOOK_VALUES.indexOf(a.outlook);
    const ob = OUTLOOK_VALUES.indexOf(b.outlook);
    if (oa !== ob) return oa - ob;
    return (a.title || '').localeCompare(b.title || '', locale);
  });

  const applyPreset = (presetId) => {
    const preset = RETURN_ASSUMPTION_PRESETS[presetId];
    if (!preset) return;
    onAssumptionsChange(cloneReturnAssumptions(preset));
    if (returnMode !== 'simple') onReturnModeChange('simple');
  };

  const handleSaveConfirm = async () => {
    const title = saveTitle.trim();
    if (!title) {
      setSaveError(t('propertyReport.futureReturns.saveTitleRequired'));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveProjection({
        title,
        outlook: saveOutlook,
        horizonYears: returnYears,
        mode: returnMode,
        assumptions: returnMode === 'simple' ? assumptions : undefined,
        advancedSchedule: returnMode === 'advanced' ? advancedSchedule : undefined,
      });
      setSaveModalOpen(false);
      setSaveTitle('');
    } catch (err) {
      setSaveError(err?.message || t('propertyReport.futureReturns.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const advancedProfile = useMemo(
    () =>
      returnMode === 'advanced'
        ? resolveAdvancedYearProfile(hypothesisBases, advancedSchedule)
        : null,
    [returnMode, hypothesisBases, advancedSchedule],
  );

  const overrideCount =
    returnMode === 'advanced' ? countAdvancedOverrides(advancedSchedule) : 0;

  const terminalNet =
    advancedProfile?.resolvedRows?.[advancedProfile.resolvedRows.length - 1]?.netIncome;

  const heroNetIncome =
    returnMode === 'advanced'
      ? terminalNet
      : hints?.revenue?.kind === 'revenueBreakdown'
        ? hints.revenue.netAtYear
        : null;

  const openAdvancedModal = () => {
    setDraftSchedule(advancedSchedule);
    setAdvancedModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedProjectionId || readOnly) return;
    if (!window.confirm(t('propertyReport.futureReturns.deleteConfirm'))) return;
    try {
      await onDeleteProjection(selectedProjectionId);
      onClearProjection();
    } catch (err) {
      console.error(err);
    }
  };

  const projectedAtYearLabel = t('propertyReport.futureReturns.projectedAtYear').replace(
    '{years}',
    String(returnYears),
  );

  return (
    <div className="mb-8 rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50/80 to-white p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-1">
        {t('propertyReport.futureReturns.title')}
      </h3>
      <p className="text-sm text-gray-600 mb-5">
        {t('propertyReport.futureReturns.intro')}
      </p>

      <div className="flex flex-col lg:flex-row lg:items-start gap-4 mb-5">
        <div className="shrink-0">
          <label className="block text-xs font-medium text-gray-600 mb-1 whitespace-nowrap">
            {t('propertyReport.futureReturns.horizonLabel')}
          </label>
          <EmptyableNumberInput
            integer
            allowEmpty={false}
            commitOnChange={false}
            min={1}
            max={MAX_RETURN_HORIZON_YEARS}
            value={returnYears}
            onChange={onReturnYearsChange}
            disabled={readOnly}
            className="w-20 px-3 py-2 border rounded-lg disabled:bg-gray-50"
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('propertyReport.futureReturns.presetsLabel')}
          </label>
          <div className="flex flex-wrap gap-2">
            {RETURN_ASSUMPTION_PRESET_IDS.map((presetId) => (
              <button
                key={presetId}
                type="button"
                disabled={readOnly}
                onClick={() => applyPreset(presetId)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-50 ${
                  activePreset === presetId
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t(`propertyReport.futureReturns.preset.${presetId}`)}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            {t('propertyReport.futureReturns.presetsHint')}
          </p>
        </div>
        <div className="flex-1 min-w-0 lg:max-w-sm xl:max-w-md">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('propertyReport.futureReturns.selectSavedScenario')}
          </label>
          <div className="flex gap-2">
            <select
              value={selectedProjectionId || ''}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) {
                  onClearProjection();
                  return;
                }
                const proj = returnProjections.find((p) => p.id === id);
                if (proj) onLoadProjection(proj);
              }}
              disabled={readOnly}
              className="flex-1 min-w-0 px-2 py-2 border rounded-lg disabled:bg-gray-50 text-sm"
            >
              <option value="">{t('propertyReport.futureReturns.noSavedScenario')}</option>
              {sortedProjections.map((p) => (
                <option key={p.id} value={p.id}>
                  [{t(OUTLOOK_I18N[p.outlook] || p.outlook)}] {p.title}
                </option>
              ))}
            </select>
            {!readOnly ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setSaveError(null);
                    setSaveModalOpen(true);
                  }}
                  className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 whitespace-nowrap shrink-0"
                >
                  {t('propertyReport.futureReturns.saveCurrentScenario')}
                </button>
                {selectedProjectionId ? (
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={handleDelete}
                    className="px-3 py-2 text-sm border border-red-200 text-red-700 rounded-lg hover:bg-red-50 shrink-0"
                    title={t('propertyReport.futureReturns.delete')}
                  >
                    ×
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-5">
        <button
          type="button"
          onClick={() => setAdvancedOptionsOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
          aria-expanded={advancedOptionsOpen}
        >
          <span>{t('propertyReport.futureReturns.advancedOptions')}</span>
          <ChevronDown
            className={`w-4 h-4 shrink-0 transition-transform ${advancedOptionsOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
        {advancedOptionsOpen ? (
          <div className="px-4 pb-4 pt-1 border-t border-gray-100">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('propertyReport.subScenario')}
            </label>
            <select
              value={selectedSubScenarioId}
              onChange={(e) => onSubScenarioChange(e.target.value)}
              className="w-full px-2 py-1.5 border rounded-lg"
            >
              <option value="">{t('propertyReport.none')}</option>
              {subScenarios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title || t('propertyReport.untitled')} ({typeLabels[s.type] || s.type})
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 mb-6 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-800 mb-5 text-center uppercase tracking-wide">
          {t('propertyReport.futureReturns.resultsTitle').replace('{years}', String(returnYears))}
        </h4>
        <div>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm mb-2 w-fit">
            <button
              type="button"
              disabled={readOnly}
              onClick={() => onReturnModeChange('simple')}
              className={`px-4 py-2 ${
                returnMode === 'simple'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              } disabled:opacity-50`}
            >
              {t('propertyReport.futureReturns.modeSimple')}
            </button>
            <button
              type="button"
              disabled={readOnly}
              onClick={() => onReturnModeChange('advanced')}
              className={`px-4 py-2 ${
                returnMode === 'advanced'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              } disabled:opacity-50`}
            >
              {t('propertyReport.futureReturns.modeAdvanced')}
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            {returnMode === 'simple'
              ? t('propertyReport.futureReturns.modeSimpleDesc')
              : t('propertyReport.futureReturns.modeAdvancedDesc')}
          </p>

          {hasSubScenario ? (
            <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4">
              {t('propertyReport.futureReturns.subScenarioFinancingNote')}
            </p>
          ) : null}

          {returnMode === 'simple' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <AssumptionCard
                label={t('propertyReport.futureReturns.revenue')}
                helpText={t('propertyReport.futureReturns.revenueHelp')}
                projectedLabel={projectedAtYearLabel}
                projectedValue={projectedAmountFromHint(hints?.revenue)}
                modes={[pctMode, dollarMode]}
                assumption={assumptions.revenue}
                onAssumptionChange={(next) =>
                  onAssumptionsChange({ ...assumptions, revenue: next })
                }
                disabled={readOnly}
              />
              <AssumptionCard
                label={t('propertyReport.futureReturns.expense')}
                helpText={t('propertyReport.futureReturns.expenseHelp')}
                projectedLabel={projectedAtYearLabel}
                projectedValue={projectedAmountFromHint(hints?.expense)}
                modes={[pctMode, dollarMode]}
                assumption={assumptions.expense}
                onAssumptionChange={(next) =>
                  onAssumptionsChange({ ...assumptions, expense: next })
                }
                disabled={readOnly}
              />
              <AssumptionCard
                label={t('propertyReport.futureReturns.propertyValue')}
                helpText={t('propertyReport.futureReturns.valueHelp')}
                projectedLabel={projectedAtYearLabel}
                projectedValue={projectedAmountFromHint(hints?.value)}
                modes={[pctMode, dollarMode, capMode]}
                assumption={assumptions.value}
                onAssumptionChange={(next) =>
                  onAssumptionsChange({ ...assumptions, value: next })
                }
                disabled={readOnly}
              />
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={readOnly}
                onClick={openAdvancedModal}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {t('propertyReport.futureReturns.advanced.configureGrid')}
              </button>
              <p className="text-sm text-gray-600">
                {en
                  ? `${overrideCount} customized year(s) of ${returnYears}`
                  : `${overrideCount} année(s) personnalisée(s) sur ${returnYears}`}
                {terminalNet != null
                  ? ` · ${t('propertyReport.futureReturns.hintNet')} : ${formatMoney(terminalNet, locale)}`
                  : ''}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 pt-5 border-t border-gray-100">
          {heroNetIncome != null ? (
            <div className="hidden sm:grid sm:grid-cols-3 sm:gap-4 mb-4 pb-4 border-b border-gray-100">
              <div aria-hidden="true" />
              <div className="text-center">
                <p className="text-xs text-gray-500">{projectedAtYearLabel}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatMoney(heroNetIncome, locale)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('propertyReport.futureReturns.heroNetCaption')}
                </p>
              </div>
              <div aria-hidden="true" />
            </div>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {heroNetIncome != null ? (
              <div className="sm:hidden text-center mb-2 pb-4 border-b border-gray-100">
                <p className="text-xs text-gray-500">{projectedAtYearLabel}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatMoney(heroNetIncome, locale)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('propertyReport.futureReturns.heroNetCaption')}
                </p>
              </div>
            ) : null}
            <MetricWithInfo
              label={`${t('propertyReport.globalReturn.prefix')} ${returnYears} ${t('propertyReport.globalReturn.suffix')}`}
              value={multiYearReturn}
              info={t('propertyReport.futureReturns.totalReturnInfo')}
              formatPercent={formatPercent}
              open={openMetricInfo === 'total'}
              onToggle={() => setOpenMetricInfo((v) => (v === 'total' ? null : 'total'))}
            />
            <MetricWithInfo
              label={t('propertyReport.annualizedReturn')}
              value={multiYearAnnualized}
              info={t('propertyReport.futureReturns.annualizedInfo')}
              formatPercent={formatPercent}
              open={openMetricInfo === 'annualized'}
              onToggle={() => setOpenMetricInfo((v) => (v === 'annualized' ? null : 'annualized'))}
            />
            <MetricWithInfo
              label={`${t('propertyReport.irrAtYear.prefix')} ${returnYears}${t('propertyReport.irrAtYear.suffix')}`}
              value={multiYearIRR}
              info={t('propertyReport.futureReturns.irrInfo')}
              formatPercent={formatPercent}
              open={openMetricInfo === 'irr'}
              onToggle={() => setOpenMetricInfo((v) => (v === 'irr' ? null : 'irr'))}
            />
          </div>
        </div>
      </div>

      <AdvancedReturnsModal
        open={advancedModalOpen}
        onClose={() => setAdvancedModalOpen(false)}
        onSave={(sched) => onAdvancedScheduleChange(sched)}
        schedule={draftSchedule}
        onScheduleChange={setDraftSchedule}
        bases={hypothesisBases}
        onPrefillFromSimple={() => {
          const filled = onPrefillAdvancedFromSimple();
          if (filled) setDraftSchedule(filled);
        }}
        onReset={() => {
          const empty = onResetAdvancedSchedule();
          if (empty) setDraftSchedule(empty);
        }}
        disabled={readOnly}
        t={t}
        language={language}
      />

      {saveModalOpen ? (
        <div
          className="fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('propertyReport.futureReturns.saveModalTitle')}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('propertyReport.futureReturns.saveTitleLabel')}
              </label>
              <input
                type="text"
                value={saveTitle}
                onChange={(e) => setSaveTitle(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder={t('propertyReport.futureReturns.saveTitlePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('propertyReport.futureReturns.saveOutlookLabel')}
              </label>
              <select
                value={saveOutlook}
                onChange={(e) => setSaveOutlook(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {OUTLOOK_VALUES.map((o) => (
                  <option key={o} value={o}>
                    {t(OUTLOOK_I18N[o])}
                  </option>
                ))}
              </select>
            </div>
            {saveError ? <p className="text-sm text-red-600">{saveError}</p> : null}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setSaveModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveConfirm}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {saving
                  ? t('propertyReport.futureReturns.saving')
                  : t('propertyReport.futureReturns.saveConfirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
